import React from 'react';
import { NextPageContext } from 'next';
import Head from 'next/head';
import Layout from '../../components/layout';
import ArticleHero from '../../components/articleHero';
import BannerAd from '../../components/ad/banner';
import SideAds, { BlockAdsOne, BlockAdsTwo } from '../../components/sideAds';
import Breadcrumb from '../../components/breadcrumb';
import SectionHeader from 'components/sectionHeader';
import pageWrapper from '../../containers/pageWrapper';
import SEO from '../../components/seo';
import FourOhFour from '../404';
import { fetchPage } from '../../utils/data/fetch';
import { fetchSupplierByURI } from '../../graphql/suppliers';
import getTitleFromPage from '../../utils/data/getTitleFromPage';
import { createSlug } from '../../utils/data/supplierDirectory';
import { wpToCF } from '../../utils/data/cdn';

interface Props {
  slug: string;
  menus: WordpressHeaderMenu[];
  supplier?: ABNSupplier;
  page: WordpressPage;
  adverts: KeyedAdverts;
}

const Top = ({ supplier }: { supplier: ABNSupplier }) => {
  const fallbackLogo = '/images/supplier_default.jpg';
  const premiumLogo =
    wpToCF(supplier.supplierDetails.logo?.mediaItemUrl) || fallbackLogo;
  const image = supplier.supplierDetails.isPremiumSupplier
    ? premiumLogo
    : fallbackLogo;
  const { isPremiumSupplier } = supplier.supplierDetails;

  return (
    <div className=' border border-abn-gray  bg-abn-gray-light p-4 grid grid-cols-7 gap-4 mb-4'>
      <div className='col-span-2 sm:col-span-1'>
        <img src={image} />
      </div>
      <div className='relative m-0 flex flex-col col-span-5 sm:col-span-6'>
        <h2
          className='flex-shrink-0 text-abn-red uppercase font-bold text-l pb-2'
          dangerouslySetInnerHTML={{ __html: supplier.title }}
        />
        {isPremiumSupplier && supplier.supplierDetails.tagline && (
          <p className='text italic mb-2'>{supplier.supplierDetails.tagline}</p>
        )}
      </div>
    </div>
  );
};

const CategoryList = ({ supplier }: { supplier: ABNSupplier }) => {
  return (
    <>
      <SectionHeader className='my-4'>Supplier Categories</SectionHeader>
      <ul>
        {supplier.supplierCategories.categories?.map((c, idx) => {
          const sub = createSlug(c.split(' > ')[1]);
          return (
            <li key={idx}>
              <a
                href={`/suppliers/${encodeURIComponent(sub)}`}
                className='inline-block text-abn-red py-1'
              >
                {c}
              </a>
            </li>
          );
        })}
      </ul>
    </>
  );
};

const ContactLine = ({
  type,
  content,
  link,
  target
}: {
  type: string;
  content: string;
  link?: string;
  target?: string;
}) => {
  const contentToRender = link ? (
    <a target={target} href={link}>
      {content}
    </a>
  ) : (
    content
  );
  return (
    <div className='flex border-b border-abn-gray pb-4 mb-4'>
      <div className='w-32 sm:w-64 flex-shrink-0'>
        <h3>{type}</h3>
      </div>
      <div className='flex-grow'>
        <p>{contentToRender}</p>
      </div>
    </div>
  );
};

const ContactInfo = ({ supplier }: { supplier: ABNSupplier }) => {
  const {
    address,
    country,
    companyContact,
    email,
    phone,
    website
  } = supplier.supplierDetails;

  return (
    <>
      <SectionHeader className='my-4'>Contact Information</SectionHeader>
      {website && (
        <ContactLine
          type='Website'
          content={website}
          link={website}
          target='_blank'
        />
      )}
      {companyContact && (
        <ContactLine type='Company contact' content={companyContact} />
      )}
      {email && (
        <ContactLine type='Email' content={email} link={`mailto:${email}`} />
      )}
      {phone && (
        <ContactLine type='Phone' content={phone} link={`tel:${phone}`} />
      )}
      {address && (
        <div className='flex border-b border-abn-gray pb-4 mb-4'>
          <div className='w-32 sm:w-64 flex-shrink-0'>
            <h3>Address</h3>
          </div>
          <div className='flex-grow'>
            <p
              dangerouslySetInnerHTML={{
                __html: address.replace(/(?:\r\n|\r|\n)/g, '<br>')
              }}
            />
          </div>
        </div>
      )}
      {country && <ContactLine type='Country' content={country} />}
    </>
  );
};

const Supplier = ({ supplier, menus, page, adverts }: Props) => {
  const title = getTitleFromPage(
    `Supplier - ${supplier?.title || 'Not found'}`
  );
  return !supplier ? (
    <FourOhFour />
  ) : (
    <>
      <Head>
        <title key='title'>{title}</title>
        <SEO yoastMeta={page.yoast_meta} ogImage={page.acf.page_image} />
      </Head>
      <Layout menus={menus}>
        <ArticleHero
          image={page.acf.page_image}
          title={`Supplier - ${supplier.title || 'Not found'}`}
        />
        <BannerAd className='mt-4 md:mb-4' advert={adverts['banner-top']} />
        <Breadcrumb href='/suppliers'>← Back to Supplier Directory</Breadcrumb>
        <div className='grid grid-cols-2 md:grid-cols-4 gap-0 sm:gap-4 mb-4'>
          <div className='col-span-3 mb-4'>
            <Top supplier={supplier} />
            {supplier.supplierDetails.isPremiumSupplier && (
              <div
                className='article'
                dangerouslySetInnerHTML={{ __html: supplier.content }}
              />
            )}
            <CategoryList supplier={supplier} />
            <ContactInfo supplier={supplier} />
          </div>
          <SideAds adverts={adverts} />
          <BlockAdsOne adverts={adverts} />
          <BlockAdsTwo adverts={adverts} />
        </div>
      </Layout>
    </>
  );
};

Supplier.getInitialProps = async ({ query }: NextPageContext) => {
  const slug = Array.isArray(query.slug) ? query.slug[0] : query.slug;
  const page = await fetchPage('suppliers');
  const { supplier } = await fetchSupplierByURI(slug);
  return { page, supplier, slug };
};

export default pageWrapper(Supplier);
